// src/components/Pentests/tabs/tools/PortScanLauncher.js
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Play } from 'lucide-react';
import { quickScanAPI } from '../../../../services/api';
import { PrimaryCTA } from '../../../common/Shinobi';
import ModuleCombobox from './ModuleCombobox';

const PORT_SCAN_MODULES = new Set(['nmap_scan', 'port_scan', 'quick_port_scan']);

export default function PortScanLauncher({ pentest }) {
  const { id: pentestIdParam } = useParams();
  const pentestId = Number(pentestIdParam);
  const navigate = useNavigate();

  const [modules, setModules] = useState([]);
  const [moduleName, setModuleName] = useState('');
  const [target, setTarget] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    quickScanAPI
      .getModules()
      .then((r) => {
        if (cancelled) return;
        const all = r?.data?.modules || [];
        const portOnly = all.filter((m) => PORT_SCAN_MODULES.has(m.name));
        setModules(portOnly);
        if (portOnly.length && !moduleName) setModuleName(portOnly[0].name);
      })
      .catch((e) => !cancelled && setError(e?.response?.data?.detail || e.message));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scopeChips = useMemo(() => {
    const sc = pentest?.scope_targets || {};
    return [
      ...(sc.ips || []),
      ...(sc.urls || []),
      ...(sc.hostnames || []),
    ].filter(Boolean);
  }, [pentest]);

  const onLaunch = async (e) => {
    e.preventDefault();
    if (!moduleName || !target.trim()) return;
    setSubmitting(true);
    setError(null);
    setInfo(null);
    try {
      const resp = await quickScanAPI.launch({
        target_value: target.trim(),
        module_name: moduleName,
        pentest_id: pentestId,
      });
      const scanId = resp?.data?.scan_id;
      setInfo(`Port scan dispatched (scan #${scanId}). View in the Runs tab.`);
      setTarget('');
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onLaunch} className="space-y-4 max-w-2xl">
      <div>
        <label className="block text-sm font-medium text-slate-800 mb-2">Module</label>
        <ModuleCombobox
          modules={modules}
          value={moduleName}
          onChange={setModuleName}
          placeholder={modules.length === 0 ? 'Loading modules...' : 'Select a port-scan module'}
          emptyMessage="No port-scan modules available"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-800 mb-2">Target</label>
        <input
          type="text"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          placeholder="198.51.100.10 or example.com"
          className="w-full bg-cyber-100 border border-cyber-300/60 rounded-lg px-3 py-2 text-slate-900 focus:border-shinobi-green outline-none"
          required
        />
        {scopeChips.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            <span className="text-xs text-slate-500 mr-1 self-center">From scope:</span>
            {scopeChips.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTarget(t)}
                className="px-2 py-0.5 text-xs rounded border border-cyber-300/60 bg-cyber-100 text-slate-700 hover:border-shinobi-green hover:text-shinobi-green"
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && <div className="text-red-600 text-sm">{error}</div>}
      {info && <div className="text-shinobi-green text-sm">{info} <button type="button" onClick={() => navigate(`/pentests/${pentestId}/runs`)} className="underline">Open Runs</button></div>}

      <div className="flex justify-end">
        <PrimaryCTA variant="green" type="submit" loading={submitting} icon={Play} iconPosition="left" disabled={modules.length === 0}>
          Launch Port Scan
        </PrimaryCTA>
      </div>
    </form>
  );
}
